import type { ReactNode } from 'react';
import type { PositionStatus } from '@nest-recruit/shared';
import { formatNumber } from '../../../lib/format';
import type { RollupCounts } from '../api/organisation-api';
import { SEAT_SEGMENTS, SeatMix, SeatSwatch } from './SeatMix';
import styles from './SeatSummary.module.css';

interface SeatSummaryProps {
  readonly counts: RollupCounts;
  readonly ventureCount: number;
  /** Opens the position ledger, filtered to one state when one is given. */
  onOpen(status?: PositionStatus): void;
}

/**
 * ORG-01's headline: every seat in one bar, with a legend whose entries open the
 * ledger at that state — 06-qol §12, every number drills through to its records.
 */
export function SeatSummary({ counts, ventureCount, onOpen }: SeatSummaryProps): ReactNode {
  return (
    <section className={styles.summary} aria-label="Seats">
      <button type="button" className={styles.total} onClick={() => onOpen()}>
        <span className={styles.totalValue}>{formatNumber(counts.total)}</span>
        <span className={styles.totalLabel}>
          {counts.total === 1 ? 'seat' : 'seats'} across {formatNumber(ventureCount)}{' '}
          {ventureCount === 1 ? 'venture' : 'ventures'}
        </span>
      </button>

      <SeatMix counts={counts} size="lg" />

      <div className={styles.legend}>
        {SEAT_SEGMENTS.map((segment) => (
          <button key={segment.key} type="button" className={styles.legendItem} onClick={() => onOpen(segment.status)}>
            <SeatSwatch tone={segment.tone} />
            {segment.label}
            <span className={styles.legendValue}>{formatNumber(counts[segment.key])}</span>
          </button>
        ))}
      </div>
    </section>
  );
}
